import Mustache from 'mustache';
import { DefinitionMap } from '../defintionMapping';
import { ListTemplate } from '../types';
import funcs from './functions';
import { renderPalletSection } from './palletSection';
import { isPalletSsccDocument, omitPalletSsccVariables } from './palletSscc';
import { BalanceSheetReportInputType, BalanceSheetReportTemplate } from './templates/balanceSheetReport.template';
import { BillInputType, BillInvoiceTemplate } from './templates/bill.invoice.template';
import { BillSettlementInputType, BillSettlementTemplate } from './templates/bill.settlement.template';
import { BuySellOrderInputType, BuySellOrderTemplate } from './templates/buySellOrder.template';
import { CustomerStatementInputType, CustomerStatementTemplate } from './templates/customerStatement.template';
import { GrowerProductTemplate } from './templates/growerProduct.template';
import { InventoryTransferTemplate } from './templates/inventoryTransfer.template';
import { ProductionRunTemplate } from './templates/productionRun.template';
import { ProfitAndLossReportInputType, profitAndLossReportTemplate } from './templates/profitAndLossReport.template';
import { PurchaseOrderInputType, PurchaseOrderTemplate } from './templates/purchaseOrder.template';
import { RemittanceInputType, RemittanceTemplate } from './templates/remittance.template';
import { SalesOrderInputType, SalesOrderOptions, SalesOrderTemplate } from './templates/salesOrder.template';

export type DocumentTemplate = {
  id?: number;
  pages: { css: string; html: string }[];
  generationOptions?: any;
  name: string;
};

export type DocumentTypePopulator<Input, Opts, Output> = {
  populate: (data: Input, options: Opts & { type: string; templateId?: number }, globalOptions: any) => Output;
  definitionMap?: DefinitionMap<Output>;
};

type DefinitionMapType<Output> = Output extends ListTemplate<infer U> ? DefinitionMap<U> : DefinitionMap<Output>;

export type TemplateComponent<
  Input,
  Output,
  Opts = void,
  Optional extends boolean = false,
  DefinitionMapOptions = void,
> = {
  templatize: Optional extends true
    ? (data: Input | undefined, options: Opts) => Output
    : (data: Input, options: Opts) => Output;
  definitionMap: DefinitionMapOptions extends void
    ? DefinitionMapType<Output>
    : (options: DefinitionMapOptions) => DefinitionMapType<Output>;
};

export enum DocumentType {
  SALES_ORDER_CONFIRMATION = 'SALES_ORDER_CONFIRMATION',
  BILL_OF_LADING = 'BILL_OF_LADING',
  PURCHASE_ORDER_CONFIRMATION = 'PURCHASE_ORDER_CONFIRMATION',
  RECEIVING_TICKET = 'RECEIVING_TICKET',
  PAYABLE = 'PAYABLE',
  LOT_SETTLEMENT = 'LOT_SETTLEMENT',
  PICK_TICKET = 'PICK_TICKET',
  SHIPMENT_CONFIRMATION = 'SHIPMENT_CONFIRMATION',
  INVOICE = 'INVOICE',
  WORK_ORDER_CONFIRMATION = 'WORK_ORDER_CONFIRMATION',
  GROWER_SETTLEMENT = 'GROWER_SETTLEMENT',
  CARRIER_CONFIRMATION = 'CARRIER_CONFIRMATION',
  MULTI_BILL_OF_LADING = 'MULTI_BILL_OF_LADING',
  PRODUCTION_RUN_REPORT = 'PRODUCTION_RUN_REPORT',
  PASSING = 'PASSING',
  TRACEABILITY_IMPORT = 'TRACEABILITY_IMPORT',
  OTHER = 'OTHER',
  UNKNOWN = 'UNKNOWN',
  CUSTOM = 'CUSTOM',
  REMITTANCE = 'REMITTANCE',
  GROWER_PRODUCT = 'GROWER_PRODUCT',
}

export enum DocumentTemplateEntityType {
  SALES_ORDER = 'SALES_ORDER',
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  BUY_SELL_ORDER = 'BUY_SELL_ORDER',
  INVOICE = 'INVOICE',
  REMITTANCE = 'REMITTANCE',
  CUSTOMER_STATEMENT = 'CUSTOMER_STATEMENT',
  GROWER_PRODUCT = 'GROWER_PRODUCT',
  PRODUCTION_RUN = 'PRODUCTION_RUN',
  INVENTORY_TRANSFER = 'INVENTORY_TRANSFER',
  GROWER_SETTLEMENT = 'GROWER_SETTLEMENT',
  BALANCE_SHEET_REPORT = 'BALANCE_SHEET_REPORT',
  PROFIT_AND_LOSS_REPORT = 'PROFIT_AND_LOSS_REPORT',
  // PAYABLE = 'PAYABLE',
}

// Define the mapping type
export type DocumentTypeMapping = {
  [DocumentTemplateEntityType.SALES_ORDER]: {
    template: typeof SalesOrderTemplate;
    input: SalesOrderInputType;
    options: SalesOrderOptions;
    documentTypes:
      | DocumentType.SALES_ORDER_CONFIRMATION
      | DocumentType.CARRIER_CONFIRMATION
      | DocumentType.BILL_OF_LADING
      | DocumentType.PICK_TICKET;
  };
  [DocumentTemplateEntityType.PURCHASE_ORDER]: {
    template: typeof PurchaseOrderTemplate;
    input: PurchaseOrderInputType;
    options: SalesOrderOptions;
    documentTypes:
      | DocumentType.PURCHASE_ORDER_CONFIRMATION
      | DocumentType.CARRIER_CONFIRMATION
      | DocumentType.BILL_OF_LADING
      | DocumentType.RECEIVING_TICKET;
  };
  [DocumentTemplateEntityType.REMITTANCE]: {
    template: typeof RemittanceTemplate;
    input: RemittanceInputType;
    options: void;
    documentTypes: DocumentType.REMITTANCE;
  };
  [DocumentTemplateEntityType.INVOICE]: {
    template: typeof BillInvoiceTemplate;
    input: BillInputType;
    options: void;
    documentTypes: DocumentType.INVOICE;
  };
  [DocumentTemplateEntityType.GROWER_SETTLEMENT]: {
    template: typeof BillSettlementTemplate;
    input: BillSettlementInputType;
    options: void;
    documentTypes: DocumentType.GROWER_SETTLEMENT;
  };
  [DocumentTemplateEntityType.BUY_SELL_ORDER]: {
    template: typeof BuySellOrderTemplate;
    input: BuySellOrderInputType;
    options: void;
    documentTypes:
      | DocumentType.BILL_OF_LADING
      | DocumentType.CARRIER_CONFIRMATION
      | DocumentType.SALES_ORDER_CONFIRMATION
      | DocumentType.PURCHASE_ORDER_CONFIRMATION
      | DocumentType.PASSING;
  };
  [DocumentTemplateEntityType.CUSTOMER_STATEMENT]: {
    template: typeof CustomerStatementTemplate;
    input: CustomerStatementInputType;
    options: void;
    documentTypes: DocumentType.OTHER;
  };
  [DocumentTemplateEntityType.GROWER_PRODUCT]: {
    template: typeof GrowerProductTemplate;
    input: any;
    options: void;
    documentTypes:
      | DocumentType.WORK_ORDER_CONFIRMATION
      | DocumentType.RECEIVING_TICKET
      | DocumentType.BILL_OF_LADING
      | DocumentType.PASSING;
  };
  [DocumentTemplateEntityType.PRODUCTION_RUN]: {
    template: typeof ProductionRunTemplate;
    input: any;
    options: void;
    documentTypes: DocumentType.PRODUCTION_RUN_REPORT;
  };
  [DocumentTemplateEntityType.INVENTORY_TRANSFER]: {
    template: typeof InventoryTransferTemplate;
    input: any;
    options: void;
    documentTypes: DocumentType.BILL_OF_LADING;
  };
  [DocumentTemplateEntityType.BALANCE_SHEET_REPORT]: {
    template: typeof BalanceSheetReportTemplate;
    input: BalanceSheetReportInputType;
    options: void;
    documentTypes: DocumentType.OTHER;
  };
  [DocumentTemplateEntityType.PROFIT_AND_LOSS_REPORT]: {
    template: typeof profitAndLossReportTemplate;
    input: ProfitAndLossReportInputType;
    options: void;
    documentTypes: DocumentType.OTHER;
  };
};

const typePopulators: {
  [K in DocumentTemplateEntityType]?: {
    populator: DocumentTypeMapping[K]['template'];
    templateDefinitions?: {
      [A in DocumentTypeMapping[K]['documentTypes']]?: { options?: DocumentTypeMapping[K]['options'] };
    };
  };
} = {
  [DocumentTemplateEntityType.SALES_ORDER]: {
    populator: SalesOrderTemplate,
    templateDefinitions: {
      [DocumentType.SALES_ORDER_CONFIRMATION]: { options: { totalUnitsType: 'ordered' } },
      [DocumentType.CARRIER_CONFIRMATION]: { options: { totalUnitsType: 'picked' } },
      [DocumentType.BILL_OF_LADING]: { options: { totalUnitsType: 'picked' } },
    },
  },
  [DocumentTemplateEntityType.PURCHASE_ORDER]: { populator: PurchaseOrderTemplate },
  [DocumentTemplateEntityType.REMITTANCE]: {
    populator: RemittanceTemplate,
    templateDefinitions: { [DocumentType.REMITTANCE]: {} },
  },
  [DocumentTemplateEntityType.INVOICE]: { populator: BillInvoiceTemplate },
  [DocumentTemplateEntityType.BUY_SELL_ORDER]: { populator: BuySellOrderTemplate },
  [DocumentTemplateEntityType.CUSTOMER_STATEMENT]: { populator: CustomerStatementTemplate },
  [DocumentTemplateEntityType.GROWER_PRODUCT]: { populator: GrowerProductTemplate },
  [DocumentTemplateEntityType.PRODUCTION_RUN]: { populator: ProductionRunTemplate },
  [DocumentTemplateEntityType.INVENTORY_TRANSFER]: { populator: InventoryTransferTemplate },
  [DocumentTemplateEntityType.GROWER_SETTLEMENT]: { populator: BillSettlementTemplate },
  [DocumentTemplateEntityType.BALANCE_SHEET_REPORT]: { populator: BalanceSheetReportTemplate },
  [DocumentTemplateEntityType.PROFIT_AND_LOSS_REPORT]: { populator: profitAndLossReportTemplate },
};

export const defaultTemplates: {
  [E in DocumentTemplateEntityType]?: {
    [D in DocumentType]?: () => Promise<{
      templateState: any;
      name: string;
      entityType: string | DocumentTemplateEntityType;
      type: string;
      pages: { html: string; css: string }[];
    }>;
  };
} = {
  [DocumentTemplateEntityType.REMITTANCE]: {
    [DocumentType.REMITTANCE]: () => import('./defaults/remittance.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.CUSTOMER_STATEMENT]: {
    [DocumentType.OTHER]: () => import('./defaults/customerStatement.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.INVOICE]: {
    [DocumentType.INVOICE]: () => import('./defaults/bill.invoice.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.SALES_ORDER]: {
    [DocumentType.BILL_OF_LADING]: () => import('./defaults/salesOrderBoL.document').then((module) => module.default),
    [DocumentType.SALES_ORDER_CONFIRMATION]: () =>
      import('./defaults/salesOrderConfirmation.document').then((module) => module.default),
    [DocumentType.PASSING]: () => import('./defaults/salesOrderPassing.document').then((module) => module.default),
    [DocumentType.PICK_TICKET]: () => import('./defaults/pickTicket.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.BUY_SELL_ORDER]: {
    [DocumentType.BILL_OF_LADING]: () =>
      import('./defaults/buySellOrderBillOfLading.document').then((module) => module.default),
    [DocumentType.SALES_ORDER_CONFIRMATION]: () =>
      import('./defaults/buySellOrderSalesConfirmation.document').then((module) => module.default),
    [DocumentType.PURCHASE_ORDER_CONFIRMATION]: () =>
      import('./defaults/buySellOrderPurchaseConfirmation.document').then((module) => module.default),
    [DocumentType.PASSING]: () => import('./defaults/buySellOrderPassing.document').then((module) => module.default),
    [DocumentType.CARRIER_CONFIRMATION]: () =>
      import('./defaults/buySellOrderCarrierConfirmation.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.PURCHASE_ORDER]: {
    [DocumentType.PURCHASE_ORDER_CONFIRMATION]: () =>
      import('./defaults/purchaseOrderConfirmation.document').then((module) => module.default),
    [DocumentType.RECEIVING_TICKET]: () =>
      import('./defaults/purchaseOrderReceivingTicket.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.GROWER_PRODUCT]: {
    [DocumentType.WORK_ORDER_CONFIRMATION]: () =>
      import('./defaults/growerProduct.document').then((module) => module.default),
    [DocumentType.RECEIVING_TICKET]: () =>
      import('./defaults/growerProductReceivingTicket.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.GROWER_SETTLEMENT]: {
    [DocumentType.GROWER_SETTLEMENT]: () =>
      import('./defaults/bill.settlement.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.PRODUCTION_RUN]: {
    [DocumentType.PRODUCTION_RUN_REPORT]: () =>
      import('./defaults/productionRun.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.INVENTORY_TRANSFER]: {
    [DocumentType.BILL_OF_LADING]: () =>
      import('./defaults/inventoryTransferBoL.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.BALANCE_SHEET_REPORT]: {
    [DocumentType.OTHER]: () => import('./defaults/balanceSheetReport.document').then((module) => module.default),
  },
  [DocumentTemplateEntityType.PROFIT_AND_LOSS_REPORT]: {
    [DocumentType.OTHER]: () => import('./defaults/profitAndLossReport.document').then((module) => module.default),
  },
};

const populate = <E extends DocumentTemplateEntityType, T extends DocumentType>(
  entityType: E,
  documentType: T,
  documentTemplate: DocumentTemplate | null,
  data: Partial<DocumentTypeMapping[E]['input']>,
  globalOptions: any,
  generationOptions: any = {}
) => {
  const typePopulator = typePopulators[entityType];

  if (!typePopulator) {
    return null;
  }

  const { populator } = typePopulator;

  return populator.populate(
    data as any,
    { ...generationOptions, type: documentType, templateId: documentTemplate?.id } as any,
    globalOptions
  );
};

const generate = async <E extends DocumentTemplateEntityType, T extends DocumentType>(
  entityType: E,
  type: T,
  documentTemplate: DocumentTemplate | null,
  data: DocumentTypeMapping[E]['input'],
  extendedData: Record<string | number, unknown> = {},
  generationOptions: any = {}
) => {
  const populatedData = populate(entityType, type, documentTemplate, data, extendedData || {}, generationOptions);

  if (!populatedData) {
    throw new Error('No populator found');
  }

  const template = documentTemplate ?? (await defaultTemplates[entityType]?.[type]?.());

  if (!template) {
    throw new Error('No template found');
  }

  const palletSection =
    type === DocumentType.PICK_TICKET && isPalletSsccDocument(extendedData)
      ? renderPalletSection(template.pages, data)
      : '';
  const lastPageIndex = template.pages.length - 1;

  const generatedPages = template.pages.map((page: any, pageIndex: number) => {
    const appended = pageIndex === lastPageIndex ? palletSection : '';
    return `<html>${Mustache.render(page.html, { ...populatedData, ...extendedData, _: funcs })}${appended}
      <style>
        font-family: 'Monseratt';
        ${page.css}
      </style>
    </html>`;
  });

  return generatedPages;
};

const getDefinitions = <E extends DocumentTemplateEntityType>(
  entityType: E,
  { isPalletSsccEnabled = false }: { isPalletSsccEnabled?: boolean } = {}
) => {
  const typePopulator = typePopulators[entityType];

  if (!typePopulator) {
    return null;
  }

  const { definitionMap } = typePopulator.populator;

  return isPalletSsccEnabled || !definitionMap ? definitionMap : omitPalletSsccVariables(definitionMap);
};

export default { generate, DocumentType, defaultTemplates, DocumentTemplateEntityType, getDefinitions };
